import type { FieldValues } from "react-hook-form";
import type { CommercialBillingScheduleBreakdownItem } from "../../../../../../types/bookings";
import type { BillingSchedule, BillingScheduleBreakdown, ServiceCharge } from "./api";
import { buildCommercialBillingScheduleBreakdownPayload } from "./buildCommercialBillingScheduleBreakdownPayload";
import type { PlanFieldItem } from "./year-card";

export type NormalizedCommercialBreakdownSnapshot = NormalizedCommercialBreakdownYear[];

type NormalizedCommercialBreakdownYear = {
  noOfYear: number;
  billing: NormalizedBillingRow[];
  serviceRows: NormalizedServiceChargeRow[];
  serviceChargePlan: NormalizedServiceChargePlan | null;
};

type NormalizedBillingRow = {
  baseRent: number | null;
  days: number | null;
  installment: number | null;
  torPeriod: number | null;
  noOfYear: number | null;
  startDate: string | null;
  endDate: string | null;
  total: number | null;
  vat: number | null;
};

type NormalizedServiceChargeRow = {
  amount: number | null;
  installment: number | null;
  startDate: string | null;
  endDate: string | null;
  dueDate: string | null;
  total: number | null;
  vat: number | null;
};

type NormalizedServiceChargePlan = {
  annualRent: string | null;
  chargeValue: string | null;
  installments: number | null;
  rentType: string | null;
};

export type CommercialBreakdownComparisonOptions = {
  isTurnoverRentOnly?: boolean;
};

function num(v: unknown): number | null {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v: unknown): string | null {
  if (v == null || v === "") return null;
  return String(v);
}

export function normalizeBillingScheduleRowForComparison(
  row: BillingSchedule | FieldValues,
  options?: CommercialBreakdownComparisonOptions,
): NormalizedBillingRow {
  const r = row as BillingSchedule;
  const ignoreBaseRentAndInstallment = options?.isTurnoverRentOnly ?? false;
  return {
    baseRent: ignoreBaseRentAndInstallment ? null : num(r.baseRent),
    days: num(r.days),
    installment: ignoreBaseRentAndInstallment ? null : num(r.installment),
    torPeriod: r.torPeriod != null ? num(r.torPeriod) : null,
    noOfYear: num(r.noOfYear),
    startDate: str(r.startDate),
    endDate: str(r.endDate),
    total: r.total != null ? num(r.total) : null,
    vat: r.vat != null ? num(r.vat) : null,
  };
}

export function normalizeServiceChargeRowForComparison(row: ServiceCharge | FieldValues): NormalizedServiceChargeRow {
  const r = row as ServiceCharge;
  return {
    amount: num(r.amount),
    installment: num(r.installment),
    startDate: str(r.startDate),
    endDate: str(r.endDate),
    dueDate: str(r.dueDate),
    total: num(r.total),
    vat: r.vat != null ? num(r.vat) : null,
  };
}

export function normalizeBillingScheduleRowsSnapshot(
  rows: BillingSchedule[] | undefined | null,
  options?: CommercialBreakdownComparisonOptions,
): NormalizedBillingRow[] {
  if (!rows?.length) return [];
  return rows.map((row) => normalizeBillingScheduleRowForComparison(row, options));
}

export function normalizeServiceChargeScheduleRowsSnapshot(
  rows: ServiceCharge[] | undefined | null,
): NormalizedServiceChargeRow[] {
  if (!rows?.length) return [];
  return rows.map(normalizeServiceChargeRowForComparison);
}

function normalizeServiceChargePlanForComparison(
  plan: BillingScheduleBreakdown["serviceChargePlan"] | CommercialBillingScheduleBreakdownItem["serviceChargePlan"],
): NormalizedServiceChargePlan | null {
  if (!plan) return null;
  return {
    annualRent: plan.annualRent != null ? String(plan.annualRent) : null,
    chargeValue: plan.chargeValue != null && plan.chargeValue !== "" ? String(plan.chargeValue) : null,
    installments: num(plan.installments),
    rentType: str(plan.rentType),
  };
}

function normalizePayloadEntry(
  entry: BillingScheduleBreakdown,
  options?: CommercialBreakdownComparisonOptions,
): NormalizedCommercialBreakdownYear {
  const billing = (entry.commercialBillingSchedule ?? []).map((row) =>
    normalizeBillingScheduleRowForComparison(row, options),
  );
  const serviceRows = (entry.commercialServiceCharge ?? []).map(normalizeServiceChargeRowForComparison);
  return {
    noOfYear: Number(entry.noOfYear),
    billing,
    serviceRows,
    serviceChargePlan: normalizeServiceChargePlanForComparison(entry.serviceChargePlan),
  };
}

/** Comparable snapshot from API-shaped breakdown payloads (save / send). */
export function normalizeCommercialBreakdownSnapshotFromPayload(
  payload: BillingScheduleBreakdown[],
  options?: CommercialBreakdownComparisonOptions,
): NormalizedCommercialBreakdownSnapshot {
  return [...payload].map((entry) => normalizePayloadEntry(entry, options)).sort((a, b) => a.noOfYear - b.noOfYear);
}

/** Comparable snapshot from live form `plans` values. */
export function normalizeCommercialBreakdownFromPlans(
  plans: unknown[] | null | undefined,
  options?: CommercialBreakdownComparisonOptions,
): NormalizedCommercialBreakdownSnapshot {
  if (!Array.isArray(plans) || plans.length === 0) return [];
  const built = buildCommercialBillingScheduleBreakdownPayload(plans as PlanFieldItem[]);
  return normalizeCommercialBreakdownSnapshotFromPayload(built, options);
}

/** Comparable snapshot from booking details `commercialBillingScheduleBreakdown`. */
export function normalizeCommercialBreakdownFromBookingDetails(
  items: CommercialBillingScheduleBreakdownItem[] | null | undefined,
  options?: CommercialBreakdownComparisonOptions,
): NormalizedCommercialBreakdownSnapshot {
  if (!items?.length) return [];

  const payload: BillingScheduleBreakdown[] = items
    .filter((item) => (item.commercialBillingSchedule?.length ?? 0) > 0)
    .map((item) => ({
      noOfYear: item.noOfYear,
      commercialBillingSchedule: item.commercialBillingSchedule as unknown as BillingSchedule[],
      commercialServiceCharge:
        item.commercialServiceCharge?.length > 0 ? (item.commercialServiceCharge as unknown as ServiceCharge[]) : [],
      ...(item.serviceChargePlan
        ? {
            serviceChargePlan: {
              annualRent: item.serviceChargePlan.annualRent,
              ...(item.serviceChargePlan.chargeValue != null && item.serviceChargePlan.chargeValue !== ""
                ? { chargeValue: item.serviceChargePlan.chargeValue }
                : {}),
              installments: item.serviceChargePlan.installments,
              rentType: item.serviceChargePlan.rentType,
            },
          }
        : {}),
    }));

  return normalizeCommercialBreakdownSnapshotFromPayload(payload, options);
}
